
import React, { useState, useRef, useEffect } from "react";
import { Menu, X } from "lucide-react";
import LanguageSelector from "../LanguageSelector";
import { Link, NavLink } from "react-router-dom";
import LoginModal from "../LoginModal";
import { useAuth } from "../AuthContext";
import { ChangePasswordModal } from "../users/ChangePasswordModal";
import { FaKey, FaLock } from 'react-icons/fa';

const AppLayout = ({ children, t, i18n }) => {
    const [isOpen, setIsOpen] = useState(false);
    const toggleMenu = () => setIsOpen(!isOpen);
    const dropdownRef = useRef();
    const [menuOpen, setMenuOpen] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const { user, logout } = useAuth();
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [isPwdModalOpen, setIsPwdModalOpen] = useState(false);
    const isRTL = t('dir') === 'rtl';


    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
        localStorage.setItem("traffic_lang", lng);
    };

    const handleLogout = () => {
        localStorage.removeItem("traffic_user");
        localStorage.removeItem("traffic_token");
        logout();
        setDropdownOpen(false);
    };
    const canAccessTraffic = (user?.Role === 1 || user?.Role === 2);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownOpen]);

    // const navLinkClass = ({ isActive }) =>
    //     isActive ? "text-[#F2A741] text-2xl font-bold" : "text-[#FDC635] text-xl font-bold hover:text-[#F2A741]";
    const navLinkClass = ({ isActive }) =>
        isActive ? "text-[#F2A741] text-2xl font-bold" : "text-[#4068AE] text-xl font-bold hover:text-[#F2A741]";

    return (
        <div
            style={{
                backgroundImage: `url(${require("../../images/background.png")})`,
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
            }}
        >
            {/* Top Bar */}
            <div
                className="w-full text-white px-4 py-2 flex items-center justify-between flex-wrap"
                style={{
                    backgroundImage: `url(${require("../../images/topbar.png")})`,
                    backgroundSize: "cover",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                }}
            >
                {/* اسم الشركة (ظاهر فقط في الديسكتوب) */}
                <span
                    className="text-xl font-bold hidden md:inline"
                    style={{ border: "2px solid #f2c142", borderRadius: "5px", padding: "10px" }}
                >
                    {t("appName")}
                </span>

                {/* حاوية اللوجو + زر احجز الآن */}
                <div className="flex items-center gap-3 md:gap-8 flex-grow md:flex-grow-0 justify-start md:justify-start">
                    <img src="/logo.png" alt="Logo" className="h-14 w-auto object-fill scale-[1.5]" />
                    <Link
                        to="https://book.abdobus.com"
                        className="hidden bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 text-black font-semibold rounded-lg shadow-lg animate-pulse
                                   hover:from-yellow-500 hover:via-yellow-400 hover:to-yellow-600 transition duration-500 ease-in-out max-w-max md:px-4 md:py-2
                                   px-3 py-1 text-sm whitespace-nowrap" style={{ whiteSpace: "nowrap", marginInlineStart: '10px' }}>
                        {t("reserveNow")}
                    </Link>
                </div>

                {/* اختيار اللغة + زر القائمة */}
                <div className="flex items-center gap-4">
                    <div className="hidden md:block">
                        <LanguageSelector
                            currentLang={i18n.language}
                            changeLanguage={(lng) => {
                                i18n.changeLanguage(lng);
                                localStorage.setItem("traffic_lang", lng);
                            }}
                        />
                    </div>

                    <button
                        onClick={toggleMenu}
                        className="md:hidden text-white focus:outline-none"
                        aria-label="Toggle menu"
                    >
                        {isOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>
            </div>

            {/* Desktop menu */}
            <div
                className="hidden md:flex items-center justify-center gap-x-4 lg:gap-x-20"
                style={{ marginTop: "5px" }}
            >
                <NavLink to="/" className={navLinkClass}>
                    {t("home")}
                </NavLink>
                <NavLink to="/About" className={navLinkClass}>
                    {t("aboutUs")}
                </NavLink>
                <NavLink to="/Services" className={navLinkClass}>
                    {t("services")}
                </NavLink>
                <NavLink to="/Jobs" className={navLinkClass}>
                    {t("applyJob")}
                </NavLink>
                <NavLink to="/Contact" className={navLinkClass}>
                    {t("contactUs")}
                </NavLink>

                {!user ? (
                    <button onClick={() => setShowLoginModal(true)} className="text-blue-600 font-medium hover:text-blue-700">
                        {t("login")}
                    </button>
                ) : (
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                            className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 font-medium focus:outline-none"
                        >
                            {user.Imagebase64 ? (
                                <img src={`data:image/png;base64,${user.Imagebase64}`} alt="avatar" className="w-8 h-8 rounded-full" />
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                                    {user.Nickname || "U"}
                                </div>
                            )}
                            <span className="truncate max-w-[120px]">{user.Username || t("user")}</span>
                        </button>

                        {dropdownOpen && (
                            <div className="absolute right-0 mt-2 w-52 bg-white border border-gray-300 rounded shadow-lg z-50">
                                <div className="px-4 py-3 border-b border-gray-200">
                                    <p className="text-gray-900 font-semibold truncate">{user?.Username}</p>
                                    {user?.Email && <p className="text-gray-600 text-sm truncate">{user.Email}</p>}
                                    {user?.RoleName && <p className="text-gray-500 text-xs mt-1">{user.RoleName}</p>}
                                </div>
                                {user?.Role === 1 && (
                                    <Link to="/Users" onClick={() => setDropdownOpen(false)} className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                        {t("users")}
                                    </Link>
                                )}
                                {canAccessTraffic && (
                                    <Link to="/TrafficJams" onClick={() => setDropdownOpen(false)} className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                        {t("trafficJams")}
                                    </Link>
                                )}
                                {user?.Role === 1 && (
                                    <Link to="/Trips" onClick={() => setDropdownOpen(false)} className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                        {t("trips")}
                                    </Link>
                                )}
                                {user?.Role === 1 && (
                                    <Link to="/DriversMap" onClick={() => setDropdownOpen(false)} className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                        {t("driverMap")}
                                    </Link>
                                )}
                                {user && (
                                    <>
                                        <hr className="my-2 border-t border-gray-200" />
                                        {/* Change Password */}
                                        <button
                                            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left"
                                            onClick={() => {
                                                setIsPwdModalOpen(true);
                                                setDropdownOpen(false);
                                            }}
                                        >
                                            <FaKey style={{ color: "#F2A741" }} />
                                            {t("changePwd")}
                                        </button>
                                    </>
                                )}
                                <>
                                    <hr className="my-2 border-t border-gray-200" />
                                    <button
                                        onClick={handleLogout}
                                        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left text-red-600"
                                    >
                                        <FaLock style={{ color: "#F2A741" }} />
                                        {t("logout")}
                                    </button>
                                </>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Mobile menu */}
            {isOpen && (
                <div dir={i18n.dir()} className="md:hidden bg-white shadow-lg rounded-b-lg z-50 px-4 py-4 space-y-2">
                    <NavLink to="/" onClick={() => setIsOpen(false)} className="block text-gray-700 font-medium">
                        {t("home")}
                    </NavLink>
                    <NavLink to="/About" onClick={() => setIsOpen(false)} className="block text-gray-700 font-medium">
                        {t("aboutUs")}
                    </NavLink>
                    <NavLink to="/Services" onClick={() => setIsOpen(false)} className="block text-gray-700 font-medium">
                        {t("services")}
                    </NavLink>
                    <NavLink to="/Jobs" onClick={() => setIsOpen(false)} className="block text-gray-700 font-medium">
                        {t("applyJob")}
                    </NavLink>
                    <NavLink to="/Contact" onClick={() => setIsOpen(false)} className="block text-gray-700 font-medium">
                        {t("contactUs")}
                    </NavLink>
                    <div className="pt-2 border-t border-gray-300">
                        <LanguageSelector
                            changeLanguage={(lng) => {
                                i18n.changeLanguage(lng);
                                localStorage.setItem("traffic_lang", lng);
                            }}
                            currentLang={i18n.language}
                        />
                        {!user && (
                            <button
                                onClick={() => {
                                    setShowLoginModal(true);
                                    setIsOpen(false);
                                }}
                                className="block text-blue-600 mt-3 font-medium"
                            >
                                {t("login")}
                            </button>
                        )}
                    </div>
                    {user && (
                        <div className="mt-4 border-t border-gray-200 pt-4">
                            {/* Clickable profile row */}
                            <button
                                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                                className="flex items-center gap-3 w-full text-left focus:outline-none"
                            >
                                {user.Imagebase64 ? (
                                    <img
                                        src={`data:image/png;base64,${user.Imagebase64}`}
                                        alt="avatar"
                                        className="w-10 h-10 rounded-full object-cover"
                                    />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                                        {(user.Username || "U")
                                            .split(" ")
                                            .map((w) => w[0])
                                            .slice(0, 2)
                                            .join("")
                                            .toUpperCase()}
                                    </div>
                                )}
                                <div className="flex flex-col">
                                    <span className="font-semibold text-gray-800">{user.Username}</span>
                                    {user.RoleName && (
                                        <span className="text-sm text-gray-500" style={{ textAlign: i18n.dir() === "rtl" ? "right" : "left" }}>
                                            {user.RoleName}
                                        </span>
                                    )}
                                </div>

                                {/* Arrow indicator */}
                                <svg
                                    className={`w-4 h-4 ml-auto transition-transform ${profileMenuOpen ? "rotate-180" : ""}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {/* Dropdown menu (Logout + Admin/TrafficJams) */}
                            {profileMenuOpen && (
                                <div className="mt-2 space-y-2">
                                    {user?.Role === 1 && (
                                        <Link
                                            to="/Users"
                                            onClick={() => {
                                                setIsOpen(false);
                                                setProfileMenuOpen(false);
                                            }}
                                            className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                                        >
                                            {t("users")}
                                        </Link>
                                    )}

                                    {canAccessTraffic && (
                                        <Link
                                            to="/TrafficJams"
                                            onClick={() => {
                                                setIsOpen(false);
                                                setProfileMenuOpen(false);
                                            }}
                                            className="block text-gray-700 hover:bg-gray-100 px-4 py-2 rounded"
                                        >
                                            {t("trafficJams")}
                                        </Link>
                                    )}
                                    {user?.Role === 1 && (
                                        <Link
                                            to="/Trips"
                                            onClick={() => {
                                                setIsOpen(false);
                                                setProfileMenuOpen(false);
                                            }}
                                            className="block text-gray-700 hover:bg-gray-100 px-4 py-2 rounded"
                                        >
                                            {t("trips")}
                                        </Link>
                                    )}
                                    {user?.Role === 1 && (
                                        <Link
                                            to="/DriversMap"
                                            onClick={() => {
                                                setIsOpen(false);
                                                setProfileMenuOpen(false);
                                            }}
                                            className="block text-gray-700 hover:bg-gray-100 px-4 py-2 rounded"
                                        >
                                            {t("driverMap")}
                                        </Link>
                                    )}

                                    {user && (
                                        <>
                                            <hr className="my-2 border-t border-gray-200" />
                                            {/* Change Password */}
                                            <button
                                                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left"
                                                onClick={() => {
                                                    setIsPwdModalOpen(true);
                                                    setIsOpen(false);
                                                    setProfileMenuOpen(false);
                                                }}
                                            >
                                                <FaKey style={{ color: "#F2A741" }} />
                                                {t("changePwd")}
                                            </button>
                                        </>
                                    )}
                                    <>
                                        <hr className="my-2 border-t border-gray-200" />
                                        <button
                                            onClick={() => {
                                                handleLogout();
                                                setIsOpen(false);
                                                setProfileMenuOpen(false);
                                            }}
                                            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left text-red-600"
                                        >
                                            <FaLock style={{ color: "#F2A741" }} />
                                            {t("logout")}
                                        </button>
                                    </>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* محتوى الصفحة */}
            <main className="p-4">{children}</main>

            {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />}
            {isPwdModalOpen && <ChangePasswordModal isOpen={isPwdModalOpen} onClose={() => setIsPwdModalOpen(false)} />}
        </div>
    );


};

// Dynamic class for nav links
const navLinkClass = ({ isActive }) =>
    isActive
        ? "text-[#f2c142] font-bold block"
        : "text-gray-200 hover:text-[#f2c142] block";

export default AppLayout;
